<template>
  <BaseDialog ref="dialog" contentClass="link-stripe-customer" :show-actions="true" @closeModal="closeDialog">
    <template slot="title">{{ $t('page.admin-subscriptions.link-dialog.title') }}</template>

    <template slot="content">
      <p class="link-dialog__intro">
        {{ $t('page.admin-subscriptions.link-dialog.intro', { name: tenant ? tenant.tenantName : '' }) }}
      </p>

      <BaseSearchBar
        v-model="search"
        name="customer-search"
        type="text"
        :placeholder="$t('page.admin-subscriptions.link-dialog.search')"
      />

      <p v-if="!candidates.length" class="link-dialog__empty">
        {{ $t('page.admin-subscriptions.link-dialog.none') }}
      </p>

      <ul v-else class="link-dialog__list">
        <li
          v-for="candidate in candidates"
          :key="candidate.stripeCustomerId"
          class="link-dialog__item"
          :class="{ 'is-selected': selected === candidate.stripeCustomerId }"
        >
          <button
            type="button"
            class="link-dialog__choice"
            :aria-pressed="String(selected === candidate.stripeCustomerId)"
            @click="selected = candidate.stripeCustomerId"
          >
            <span class="link-dialog__name" :title="candidate.name">{{
              candidate.direct
                ? $t('page.admin-subscriptions.link-dialog.direct')
                : candidate.name || $t('page.admin-subscriptions.link-dialog.unnamed')
            }}</span>
            <span class="link-dialog__email" :title="candidate.email">{{ candidate.email || '—' }}</span>
            <span class="link-dialog__id">{{ candidate.stripeCustomerId }}</span>
            <span v-if="candidate.confidence" class="link-dialog__confidence" :class="`is-${candidate.confidence}`">
              {{ $t(`page.admin-subscriptions.link-dialog.confidence.${candidate.confidence}`) }}
            </span>
          </button>
        </li>
      </ul>

      <p v-if="error" class="link-dialog__error">{{ error }}</p>
    </template>

    <template slot="actions">
      <BaseButton isOutlined @click.native="closeDialog">{{ $t('actions.cancel') }}</BaseButton>
      <BaseButton :disabled="!selected || submitting" @click.native="submit">
        {{ $t('page.admin-subscriptions.link-dialog.confirm') }}
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseSearchBar from '@/components/base/BaseSearchBar';
import { mapActions, mapGetters } from 'vuex';
import { matchConfidence, rankConfidence } from '@/util/billing-match';

function statusOf(error) {
  return error && error.response ? error.response.status : null;
}

/**
 * Attaches a Stripe customer to a tenant — the step that replaces a CSV review.
 *
 * Every candidate is a *proposal*. Nothing auto-selects, because at 29 customers a human confirming
 * is cheaper than a mislink, and a mislink shows one tenant another tenant's billing state. The
 * backend refuses a re-point regardless (a `tenant_id IS NULL` guard plus a partial unique index),
 * so this dialog cannot cause one even if it tried — it surfaces the refusal as a message.
 */
export default {
  name: 'LinkStripeCustomerDialog',
  components: { BaseButton, BaseDialog, BaseSearchBar },
  data() {
    return {
      tenant: null,
      selected: null,
      search: '',
      submitting: false,
      error: null,
    };
  },
  computed: {
    ...mapGetters('billing', ['unlinkedCustomers']),

    /**
     * Unlinked customers, ranked by how confidently they match this tenant.
     *
     * Mirrors the backfill heuristic from the plan, in the order that actually works on this data:
     * an exact email match is strongest, a name match next, a shared email domain last. Domain
     * matching does most of the work in practice, because a lot of these billing addresses are
     * accounts-payable mailboxes rather than app users — `payable@`, `comptabilite@`, `achats@`.
     */
    candidates() {
      if (!this.tenant) return [];

      const term = this.search.trim().toLowerCase();
      const tenantName = (this.tenant.tenantName || '').toLowerCase();

      const matches = this.unlinkedCustomers
        .map((c) => ({ ...c, confidence: matchConfidence(c, tenantName) }))
        .filter((c) => {
          if (!term) return true;
          return [c.name, c.email, c.stripeCustomerId]
            .filter(Boolean)
            .some((field) => field.toLowerCase().includes(term));
        })
        .sort((a, b) => rankConfidence(b.confidence) - rankConfidence(a.confidence));

      // A pasted customer id that is not in the list yet is still linkable: the backend fetches it
      // from Stripe first. This is how the very first customer gets linked before any import, and
      // how a single tenant gets tested end to end. Case preserved — Stripe ids are case-sensitive.
      const pasted = this.search.trim();
      if (/^cus_[A-Za-z0-9]+$/.test(pasted) && !matches.some((c) => c.stripeCustomerId === pasted)) {
        matches.unshift({ stripeCustomerId: pasted, name: null, email: null, direct: true });
      }

      return matches;
    },
  },
  watch: {
    /**
     * A pasted customer id is a choice already made — asking for a second click on the row it
     * produces was the friction the first hands-on run tripped over. Select it as soon as it looks
     * like one; drop it again if the text is edited into something else, unless it names a listed
     * customer the operator picked on purpose.
     */
    search(value) {
      const pasted = value.trim();
      if (/^cus_[A-Za-z0-9]+$/.test(pasted)) {
        this.selected = pasted;
      } else if (this.selected && !this.candidates.some((c) => c.stripeCustomerId === this.selected)) {
        // Filtered out of view: confirming would link a customer the list no longer shows.
        this.selected = null;
      }
    },
  },
  methods: {
    ...mapActions('billing', ['getUnlinkedCustomers', 'linkCustomer']),

    openDialog(tenant) {
      this.tenant = tenant;
      this.selected = null;
      this.search = '';
      this.error = null;
      this.getUnlinkedCustomers();
      this.$refs.dialog.openModal();
    },

    /**
     * BaseDialog drives visibility through its own `isModalOpen`, and emits `closeModal` when the
     * user dismisses it. Setting the flag covers both paths — the button here and an Esc key — so
     * state is cleared either way.
     */
    closeDialog() {
      this.$refs.dialog.isModalOpen = false;
      this.reset();
    },

    reset() {
      this.tenant = null;
      this.selected = null;
      this.search = '';
      this.error = null;
      this.submitting = false;
    },

    submit() {
      if (!this.selected || !this.tenant || this.submitting) return Promise.resolve();

      this.submitting = true;
      this.error = null;

      return this.linkCustomer({ tenantId: this.tenant.tenantId, stripeCustomerId: this.selected })
        .then(() => {
          this.$emit('action-succeeded');
          this.closeDialog();
        })
        .catch((e) => {
          // 409 is the expected failure — someone picked a customer, or a tenant, that is already
          // linked. Naming it precisely is the difference between a fixable mistake and a mystery.
          this.error =
            statusOf(e) === 409
              ? this.$t('page.admin-subscriptions.link-dialog.conflict')
              : statusOf(e) === 404
              ? this.$t('page.admin-subscriptions.link-dialog.unknown')
              : this.$t('page.admin-subscriptions.link-dialog.failed');
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style lang="scss">
.base-dialog__link-stripe-customer {
  // Four columns per row — name, email, id, confidence — need more than the default side panel.
  // Same per-dialog override the tender dialog uses.
  width: 42rem;
  max-width: 100vw;

  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .base-dialog__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 32px;
  }
}

.link-dialog {
  &__intro {
    margin-bottom: 16px;
  }

  &__list {
    list-style: none;
    margin: 16px 0 0;
    padding: 0;
    max-height: 320px;
    overflow-y: auto;
  }

  &__item {
    border-bottom: 1px solid $color-neutral-pale;

    &.is-selected {
      background: $color-neutral-pale;
      box-shadow: inset 3px 0 0 $color-accent;
    }
  }

  &__choice {
    display: grid;
    // minmax(0, …) lets the two text columns shrink below their content so the id and the tag,
    // which must stay readable, never push past the edge. Overflowing text is clipped with an
    // ellipsis and the full value is in the title.
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) auto auto;
    gap: 12px;
    align-items: center;
    width: 100%;
    padding: 10px 8px;
    background: none;
    border: 0;
    cursor: pointer;
    text-align: left;
    font-size: 13px;
  }

  &__name,
  &__email {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__id {
    font-family: monospace;
    opacity: 0.6;
    font-size: 12px;
    white-space: nowrap;
  }

  &__confidence {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;

    &.is-strong {
      color: #2c6544;
    }

    &.is-medium {
      color: #9c5a0b;
    }

    &.is-weak {
      opacity: 0.5;
    }
  }

  &__empty,
  &__error {
    margin-top: 16px;
    font-size: 13px;
  }

  &__error {
    color: #9b2a2a;
  }
}
</style>
